import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, ChevronRight, Activity, Beaker, Sparkles, 
  Calendar, ArrowUpRight
} from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { reportsAPI } from '../lib/api';
import { formatDate, reportTypeLabel } from '../lib/utils';

const reportConfig = {
  gut_microbiome: {
    icon: Activity,
    gradient: 'from-[#8DA399] to-[#6B8A7A]',
    bgGradient: 'from-[#8DA399]/10 to-[#8DA399]/5',
    color: '#8DA399',
    illustration: '🦠',
  },
  vaginal_microbiome: {
    icon: Beaker,
    gradient: 'from-[#E8A095] to-[#D68C82]',
    bgGradient: 'from-[#E8A095]/10 to-[#E8A095]/5',
    color: '#E8A095',
    illustration: '🔬',
  },
  other: {
    icon: FileText,
    gradient: 'from-[#95B8E8] to-[#7AA3D6]',
    bgGradient: 'from-[#95B8E8]/10 to-[#95B8E8]/5',
    color: '#95B8E8',
    illustration: '📋',
  },
};

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const response = await reportsAPI.getAll();
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="reports-page">
        {/* Simple Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-[#4A2C2A]">Health Reports</h1>
            <p className="text-[#4A2C2A]/60 mt-1">
              Your personalized health reports with detailed analysis
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm text-[#4A2C2A]/60">
            <span>{reports.length} report{reports.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Reports Grid */}
        {reports.length === 0 ? (
          <div className="relative overflow-hidden soft-card p-16 text-center">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#E8A095]/5 to-transparent rounded-full blur-3xl" />
            <div className="relative z-10">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-[#E8A095]/20 to-[#E8A095]/10 flex items-center justify-center mx-auto mb-8">
                <span className="text-5xl">🔬</span>
              </div>
              <h2 className="font-heading text-2xl text-[#4A2C2A] mb-3">No Reports Yet</h2>
              <p className="text-[#4A2C2A]/60 max-w-md mx-auto mb-8">
                Your health reports will appear here once they're uploaded by your wellness provider. 
                Each report includes detailed analysis with personalized insights.
              </p>
              <div className="flex items-center justify-center gap-8 text-sm text-[#4A2C2A]/50">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  <span>Gut Microbiome</span>
                </div>
                <div className="flex items-center gap-2">
                  <Beaker className="w-4 h-4" />
                  <span>Vaginal Microbiome</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span>More Coming Soon</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {reports.map((report, index) => {
              const config = reportConfig[report.report_type] || reportConfig.other;
              const Icon = config.icon;
              const displayDate = report.report_date || report.created_at;
              
              return (
                <Link
                  key={report.id}
                  to={`/reports/${report.id}`}
                  className="group relative overflow-hidden bg-white rounded-2xl border border-[#E8A095]/10 hover:border-[#E8A095]/30 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  data-testid={`report-${report.id}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Background gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${config.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                  
                  {/* Top colored bar */}
                  <div className={`h-2 bg-gradient-to-r ${config.gradient}`} />
                  
                  <div className="relative p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-[#4A2C2A]/30 group-hover:text-[#E8A095] transition-colors" />
                    </div>
                    
                    <h3 className="font-heading text-xl text-[#4A2C2A] mb-1 group-hover:text-[#4A2C2A] transition-colors">
                      {report.title}
                    </h3>
                    <p className="text-sm font-medium mb-4" style={{ color: config.color }}>
                      {reportTypeLabel(report.report_type)}
                    </p>
                    
                    {report.summary && (
                      <p className="text-sm text-[#4A2C2A]/70 leading-relaxed mb-4 line-clamp-2">
                        {report.summary}
                      </p>
                    )}
                    
                    {/* Quick insights preview */}
                    {report.analysis?.key_findings?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {report.analysis.key_findings.slice(0, 2).map((finding, i) => (
                          <span key={i} className="px-3 py-1 bg-[#FDFBF7] text-[#4A2C2A]/70 rounded-lg text-xs truncate max-w-[200px]">
                            {finding}
                          </span>
                        ))}
                        {report.analysis.key_findings.length > 2 && (
                          <span className="px-3 py-1 bg-[#E8A095]/10 text-[#E8A095] rounded-lg text-xs font-medium">
                            +{report.analysis.key_findings.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-4 border-t border-[#E8A095]/10">
                      <div className="flex items-center gap-4 text-xs text-[#4A2C2A]/50">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(displayDate)}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#E8A095] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        View Details <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Info section */}
        {reports.length > 0 && (
          <div className="bg-gradient-to-r from-[#FDFBF7] to-white rounded-2xl p-6 flex items-center gap-6">
            <div className="w-12 h-12 rounded-xl bg-[#E8A095]/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-[#E8A095]" />
            </div>
            <div>
              <h3 className="font-medium text-[#4A2C2A] mb-1">Personalized Insights</h3>
              <p className="text-sm text-[#4A2C2A]/60">
                Each report provides detailed analysis and personalized recommendations based on your health profile and goals.
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
